export const toSafeNumber = (value, defaultValue = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : defaultValue;
};

export const formatMinutesAsHHMM = (minutesValue, fallback = "-") => {
  const minutes = Number(minutesValue);
  if (Number.isNaN(minutes)) return fallback;

  const sign = minutes < 0 ? "-" : "";
  const absMinutes = Math.abs(Math.round(minutes));
  const hours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(
    2,
    "0",
  )}`;
};

export const formatMinutesAsDays = (
  minutesValue,
  dailyMinutes = 420,
  fallback = "-",
) => {
  const minutes = Number(minutesValue);
  const dayMinutes = Number(dailyMinutes);
  if (Number.isNaN(minutes) || Number.isNaN(dayMinutes) || dayMinutes <= 0) {
    return fallback;
  }

  const days = minutes / dayMinutes;
  const rounded = Math.round(days * 100) / 100;
  return `${rounded} يوم`;
};

/**
 * يطابق الـ API عدة مفاتيح لرقم نوع الإجازة (vid / vac_id / vacationstype_id ...).
 */
export const vacationTypeIdFromRecord = (record) => {
  if (!record || typeof record !== "object") return null;
  const keys = [
    "vid",
    "vac_id",
    "vacationtype_id",
    "vacation_type_id",
    "vacationstype_id",
    "type_id",
  ];
  for (const k of keys) {
    const v = record[k];
    if (v === undefined || v === null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

export const findVacationBalanceByType = (tasksAmount, vacationTypeId) => {
  const target = Number(vacationTypeId);
  if (!Number.isFinite(target) || target <= 0) return null;
  const list = Array.isArray(tasksAmount) ? tasksAmount : [];

  // نُفضّل سجلات "الرصيد" (vid / vacationstype_id) على سجلات الاستهلاك/الربط (vac_id)
  // لتجنب اختيار صف قد يُظهر رصيدًا سالبًا بدل الرصيد الفعلي لنوع الإجازة.
  const primaryMatch =
    list.find((record) => {
      const raw = record?.vid ?? record?.vacationstype_id;
      const id = Number(raw);
      return Number.isFinite(id) && id === target;
    }) || null;
  if (primaryMatch) return primaryMatch;

  return list.find((record) => vacationTypeIdFromRecord(record) === target) || null;
};

/** عرض الممنوحة/المتبقية في نافذة الإجازة: بدل الرقم 0 يظهر 00:00 */
export const formatModalBalanceDisplay = (v) => {
  if (v === null || v === undefined) return "00:00";
  if (v === "-") return "-";
  if (v === 0 || v === "0") return "00:00";
  return v;
};

export const buildGivenRestDisplay = (
  tasksAmount,
  vacationTypeId,
  options = {},
) => {
  const { unit = "hhmm", dailyMinutes = 420 } = options;
  const selectedBalance = findVacationBalanceByType(
    tasksAmount,
    vacationTypeId,
  );

  if (!selectedBalance) {
    return { given: "-", rest: "-", balance: null };
  }

  const given =
    selectedBalance.amount_m === undefined || selectedBalance.amount_m === null
      ? "-"
      : unit === "days"
        ? formatMinutesAsDays(selectedBalance.amount_m, dailyMinutes)
        : formatMinutesAsHHMM(selectedBalance.amount_m);

  const rest =
    selectedBalance.rest === undefined || selectedBalance.rest === null
      ? "-"
      : unit === "days"
        ? formatMinutesAsDays(selectedBalance.rest, dailyMinutes)
        : formatMinutesAsHHMM(selectedBalance.rest);

  return { given, rest, balance: selectedBalance };
};

/** جمع amount_m أو rest لجميع الأنواع (إجمالي نافذة الفترة من given-tasks) */
export const sumTasksAmountField = (tasksAmount, field) => {
  const list = Array.isArray(tasksAmount) ? tasksAmount : [];
  return list.reduce((sum, row) => sum + toSafeNumber(row?.[field], 0), 0);
};

/** إجازة الإعفاء: الرصيد الافتراضي 21 ساعة؛ المتبقي = 21:00 − الممنوح */
export const EXEMPTION_VACATION_DEFAULT_MINUTES = 21 * 60;

export const parseBalanceHHMMToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const m = value.trim().match(/^(-)?(\d+):(\d{2})$/);
  if (!m) return null;
  const sign = m[1] ? -1 : 1;
  const hours = Number(m[2]);
  const mins = Number(m[3]);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null;
  return sign * (hours * 60 + mins);
};

export const normalizeArabicVacationLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[إأآ]/g, "ا")
    .replace(/ى/g, "ي")
    .trim();

export const isExemptionVacationLabel = (label) =>
  normalizeArabicVacationLabel(label).includes("اعفاء");

export const buildExemptionRestFromGivenDisplay = (
  givenHHMM,
  fallback = "00:00",
) => {
  const givenMinutes = Math.max(0, parseBalanceHHMMToMinutes(givenHHMM) || 0);
  const restMinutes = Math.max(
    0,
    EXEMPTION_VACATION_DEFAULT_MINUTES - givenMinutes,
  );
  return formatMinutesAsHHMM(restMinutes, fallback);
};
